import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { useMagnetic } from './interactions';
import { Chip, TechIcon } from './TechMarks';
import './App.css';

gsap.registerPlugin(ScrollTrigger);

/* Afrah's own Spline scenes. Both are heavy, so they load off the critical
   path and only mount once their section is close to the viewport. */
const Spline = lazy(() => import('@splinetool/react-spline'));
const SCENE_BADGE = 'https://prod.spline.design/I1XD1NhZSn9mNwPv/scene.splinecode';
const SCENE_KEYPAD = 'https://draft.spline.design/2-Z11PBJSEC-2oeW/scene.splinecode';

/** Mounts children only once the wrapper nears the viewport. */
const NearViewport = ({ rootMargin = '400px', className, children }) => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setNear(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return <div ref={ref} className={className}>{near ? children : null}</div>;
};


const asset = (p) => `${process.env.PUBLIC_URL}${p}`;

const wowPizzaImg = asset('/assets/projects/wowpizza_view.png');
const mahmudRajaImg = asset('/assets/projects/mahmudraja_view.png');
// const bearyImg = asset('/assets/projects/beary_view.jpeg');
const timezoneShot = asset('/assets/projects/home_page2.png');
const maneIcon = asset('/assets/projects/mane_view.jpeg');
const forestIcon = asset('/assets/projects/forest_view.jpeg');
/* ---------------------------------------------------------------------------
   Motion. GSAP only, no Motion library in the same tree, no scroll listeners.
   Reduced motion short-circuits every effect below.
   ------------------------------------------------------------------------ */

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Scroll-reveal for anything marked [data-reveal].
 *  Deliberately NOT ScrollTrigger: the two Spline scenes land late and change
 *  the page height, which strands a `once:true` batch and leaves whole columns
 *  invisible. An IntersectionObserver re-evaluates against live layout, so
 *  content can never get stuck at opacity 0. */
const useReveal = (scope) => {
  useEffect(() => {
    const root = scope.current;
    if (!root) return undefined;

    const nodes = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!nodes.length) return undefined;

    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      nodes.forEach((n) => n.classList.add('is-in'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [scope]);
};

/* --- Navigation ---------------------------------------------------------- */

const NAV = [
  { label: 'Home',       id: null },
  { label: 'About Me',   id: 'about' },
  { label: 'Experience', id: 'work' },
  { label: 'Projects',   id: 'projects' },
  { label: 'Skills',     id: 'stack' },
  { label: 'Contact',    id: 'contact' },
];

const Nav = () => {
  const [stuck, setStuck] = useState(false);
  const [active, setActive] = useState('Home');
  const sentinel = useRef(null);

  // Sticky state from a sentinel, not a scroll handler.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => setStuck(!e.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Active link from section visibility, also observer-driven.
  useEffect(() => {
    const ids = NAV.map((n) => n.id).filter(Boolean);
    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!nodes.length) return undefined;

    const visible = new Map();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target.id, e.intersectionRatio));
        let bestId = null;
        let bestRatio = 0;
        visible.forEach((ratio, id) => {
          if (ratio > bestRatio) { bestRatio = ratio; bestId = id; }
        });
        if (!bestId || bestRatio === 0) { setActive('Home'); return; }
        setActive(NAV.find((n) => n.id === bestId)?.label ?? 'Home');
      },
      { rootMargin: '-88px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    if (!id) {
      window.scrollTo({ top: 0, behavior: prefersReduced() ? 'auto' : 'smooth' });
      return;
    }
    document.getElementById(id)?.scrollIntoView({
      behavior: prefersReduced() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <>
      <div ref={sentinel} className="nav-sentinel" aria-hidden="true" />
      <header className={`nav${stuck ? ' nav--stuck' : ''}`}>
        <button className="nav-brand" onClick={() => go(null)}>
          <span className="nav-brand-mark">AM</span>
          <span className="nav-brand-name">Afrah Mahmud</span>
        </button>

        <nav className="nav-links" aria-label="Sections">
          {NAV.map(({ label, id }) => (
            <button
              key={label}
              className={`nav-link${active === label ? ' is-active' : ''}`}
              aria-current={active === label ? 'true' : undefined}
              onClick={() => go(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <a
          className="nav-cta"
          href={asset('/resume.pdf')}
          target="_blank"
          rel="noopener noreferrer"
        >
          Resume <span aria-hidden="true">↗</span>
        </a>
      </header>
    </>
  );
};

/* --- Hero: pinned stage, kinetic platform slot, cinematic scrub ------------
   The headline claims four platforms, so the headline itself cycles through
   them. The stage pins for 180vh while the product shot pushes in and the
   copy parallaxes out, then releases into the marquee.
   ------------------------------------------------------------------------ */

const SOCIALS = [
  { label: 'GitHub',   href: 'https://github.com/Afrah196', icon: 'GitHub' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/afrah-mahmud-965797295/', icon: 'LinkedIn' },
];

const HERO_STATS = [
  { v: '4',       k: 'platforms shipped' },
  { v: '10,000+', k: 'developers on the SDKs' },
  { v: '3.8→4.6', k: 'store rating lifted' },
  { v: '99.98%',  k: 'crash-free sessions' },
];

const Hero = () => {
  const stage = useRef(null);
  const magnetA = useMagnetic({ strength: 0.3 });
  const magnetB = useMagnetic({ strength: 0.26 });

  useLayoutEffect(() => {
    if (prefersReduced() || !stage.current) return undefined;

    /* Entrance is CSS keyframes, not GSAP: a heavy WebGL scene can starve
       requestAnimationFrame on slow hardware, and a starved rAF would leave
       the hero copy invisible. CSS animations are immune to that. Only the
       scroll parallax stays on GSAP, where being slow is harmless. */
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '[data-h-scene]',
        { yPercent: 0 },
        {
          yPercent: -9,
          ease: 'none',
          scrollTrigger: {
            trigger: stage.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        }
      );
    }, stage);
    return () => ctx.revert();
  }, []);

  return (
    <section className="hero" ref={stage}>
      <div className="hero-sky" aria-hidden="true" />

      <div className="hero-inner">
        <div className="hero-copy">
          <p className="eyebrow" data-h-fade>Mobile and web engineer</p>

          <h1 className="hero-title">
            <span className="line"><span data-h-line>I build apps that ship</span></span>
            <span className="line">
              <span data-h-line>on <span className="em">four platforms</span>.</span>
            </span>
          </h1>

          <p className="hero-sub" data-h-fade>
            Android, iOS, Flutter and React. I build cross-platform SDKs used by
            10,000+ developers, and full-stack web products end to end.
          </p>

          <div className="hero-actions" data-h-fade>
            <a className="btn btn-solid" href="mailto:afrahmahmud2014@gmail.com" ref={magnetA}>
              Get in touch
            </a>
            <a
              className="btn btn-line"
              href={asset('/resume.pdf')}
              target="_blank"
              rel="noopener noreferrer"
              ref={magnetB}
            >
              Resume <span aria-hidden="true">↗</span>
            </a>

            <span className="hero-socials">
              {SOCIALS.map((sc) => (
                <a
                  key={sc.label}
                  href={sc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={sc.label}
                >
                  <TechIcon name={sc.icon} size={17} />
                </a>
              ))}
            </span>
          </div>
        </div>

        {/* The badge: one element of the hero, sized to its own column. */}
        <div className="hero-scene" data-h-scene>
          <Suspense fallback={<div className="scene-loading" aria-hidden="true" />}>
            <Spline scene={SCENE_BADGE} onLoad={() => ScrollTrigger.refresh()} />
          </Suspense>
        </div>
      </div>

      <ul className="hero-rail">
        {HERO_STATS.map((st) => (
          <li key={st.k} data-h-stat>
            <span className="m-v">{st.v}</span>
            <span className="m-k">{st.k}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/* --- Stack marquee, directly under the hero ------------------------------- */

const MARQUEE = [
  'Kotlin', 'Swift', 'Dart', 'Flutter', 'Android SDK', 'iOS', 'React.js',
  'TypeScript', 'JavaScript', 'Tailwind CSS', 'Vite', 'Python', 'FastAPI',
  'PostgreSQL', 'Firebase', 'AWS', 'WebRTC', 'Vercel',
];

const Marquee = () => (
  <section className="marquee" aria-label="Technologies I work with">
    <div className="marquee-track">
      {[0, 1].map((copy) => (
        <ul className="marquee-run" key={copy} aria-hidden={copy === 1}>
          {MARQUEE.map((t) => (
            <li key={t} className="marquee-item">
              <TechIcon name={t} size={19} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      ))}
    </div>
  </section>
);

/* --- Section header ------------------------------------------------------- */

const Head = ({ eyebrow, title, children }) => (
  <header className="sec-head" data-reveal>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="sec-title">{title}</h2>
    {children ? <p className="sec-lede">{children}</p> : null}
  </header>
);

/* --- Projects: vertical scroll drives a horizontal pan --------------------
   Canonical pin + scrub. Falls back to a plain stacked grid whenever motion
   is reduced or the viewport is too narrow for a hijack to be pleasant.
   ------------------------------------------------------------------------ */

const useIsNarrow = (px = 1080) => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < px
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`);
    const on = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [px]);
  return narrow;
};

/* Sits above the track in both layouts. In the panning one it is inside the
   pinned area, so it holds still at the top while the panels run past under it. */
const ProjectsHead = ({ hint }) => (
  <header className="pan-head">
    <div>
      <p className="eyebrow">Projects</p>
      <h2 className="sec-title">Selected <span className="em">work</span>.</h2>
    </div>
    {hint ? <p className="pan-hint" aria-hidden="true">Keep scrolling</p> : null}
  </header>
);

const ProjectsPan = ({ items }) => {
  const wrap = useRef(null);
  const track = useRef(null);
  const narrow = useIsNarrow();
  const hijack = !narrow && !prefersReduced();

  useLayoutEffect(() => {
    if (!hijack || !wrap.current || !track.current) return undefined;

    const ctx = gsap.context(() => {
      const distance = () => track.current.scrollWidth - window.innerWidth;

      gsap.to(track.current, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: wrap.current,
          start: 'top top',
          end: () => `+=${distance()}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
    }, wrap);

    const settle = setTimeout(() => ScrollTrigger.refresh(), 350);
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);

    /* Re-measure whenever the track's real width lands, rather than trusting a
       single timer: a distance of zero pins the section but never pans it. */
    let last = track.current.scrollWidth;
    const ro = new ResizeObserver(() => {
      const now = track.current?.scrollWidth ?? 0;
      if (Math.abs(now - last) > 1) { last = now; ScrollTrigger.refresh(); }
    });
    ro.observe(track.current);

    return () => {
      clearTimeout(settle);
      window.removeEventListener('load', onLoad);
      ro.disconnect();
      ctx.revert();
    };
  }, [hijack, items.length]);

  if (!hijack) {
    return (
      <div className="proj-stack">
        <ProjectsHead />
        {items.map((p) => (
          <article className={`proj${p.portrait ? ' proj--portrait' : ''}`} key={p.title}>
            <ProjectShot p={p} />
            <ProjectBody p={p} />
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="pan" ref={wrap}>
      <ProjectsHead hint />

      <div className="pan-track" ref={track}>
        {items.map((p) => (
          <article
            className={`pan-panel${p.portrait ? ' pan-panel--portrait' : ''}`}
            key={p.title}
          >
            <ProjectShot p={p} />
            <ProjectBody p={p} />
          </article>
        ))}
      </div>
    </div>
  );
};

/* --- App ------------------------------------------------------------------ */

/* Grouped by theme with a metric strip, so a recruiter can scan a role in a
   few seconds instead of reading seven undifferentiated bullets. */
const jobs = [
  {
    company: 'Exotel',
    role: 'Member of Technical Staff',
    grade: 'MTS-1',
    period: 'Jul 2024 to now',
    location: 'Bengaluru',
    type: 'Full-time',
    metrics: [
      { v: '3.8→4.6', k: 'store rating' },
      { v: '10,000+', k: 'developers' },
      { v: '99.98%',  k: 'crash-free' },
      { v: '3 wk→5 d', k: 'integration time' },
    ],
    groups: [
      {
        label: 'Mobile',
        points: [
          'Re-architected the Flutter plugin end to end and published the sample app to the Play Store against Android 14 and 15.',
          'Cut the Android crash rate 40% fixing OEM-specific, network-switch and media-session bugs; added 16KB page support.',
          'Shipped Android, iOS and Flutter SDKs to 10,000+ developers at 99.98% crash-free.',
        ],
      },
      {
        label: 'Web',
        points: [
          'Built multi-instance support in a single browser tab for the Web SDK.',
          'Added device selection, codec preference and granular volume APIs.',
        ],
      },
      {
        label: 'SDK craft',
        points: [
          '85% code reuse across four platforms; semantic versioning across 5 major releases.',
          'Halved SDK init time from 800ms to 400ms.',
          'Remediated exposed API keys in public repos and hardened WebViews for enterprise review.',
        ],
      },
      {
        label: 'On-call',
        points: [
          'Primary DRI for 12 weeks: 1,500+ production incidents at 99.9%+ availability across 6 regions.',
        ],
      },
    ],
    tags: ['Kotlin', 'Swift', 'Dart', 'Flutter', 'TypeScript', 'Android SDK', 'iOS / CallKit', 'WebRTC'],
  },
  {
    company: 'Exotel',
    role: 'Tech Intern',
    grade: null,
    period: 'Jan 2024 - Jul 2024',
    location: 'Bengaluru',
    type: 'Internship',
    metrics: [
      { v: '85%', k: 'test coverage' },
      { v: '−35%', k: 'API latency' },
      { v: '5,000+', k: 'beta users' },
    ],
    groups: [
      {
        label: 'Build',
        points: [
          'Real-time features in Flutter and Java, reaching 40% adoption among 5,000+ beta users.',
          'Cut API response times 35% with better data structures and caching.',
        ],
      },
      {
        label: 'Quality',
        points: [
          'Automated test suite at 85% coverage with JUnit and Espresso, cutting post-release bugs 25%.',
        ],
      },
    ],
    tags: ['Flutter', 'Java', 'JUnit', 'Espresso', 'REST APIs', 'MySQL'],
  },
];


/* Facts below come from each project's own README, store listing and source.
   `shot` is a real capture; `icon` marks a slot still waiting on one. */
/* Order is deliberate: real client work first, then the shipped store app,
   then personal builds. Facts come from each project's own README, store
   listing and source. `shot` is a real capture; `icon` marks a slot still
   waiting on one. */
const leadProject = {
  title: 'Wow Pizza',
  subtitle: 'Ordering platform for a working pizza business',
  period: 'Sep 2025 to Jan 2026',
  kind: 'Web application',
  context: 'For a real business',
  tags: ['React.js', 'Python', 'FastAPI', 'PostgreSQL', 'AWS SNS', 'Firebase'],
  points: [
    'Built for an actual pizza business and running live on its own domain, not a demo',
    'Full ordering flow on 65+ REST endpoints, from menu and cart through to checkout',
    'Delivery charges calculated live from the customer\u2019s address via the Google Maps API',
    'Admin dashboard behind JWT auth, with order alerts by email, SMS and push',
    'Deployed to Vercel and Render with SSL on a custom domain',
  ],
  shot: wowPizzaImg,
  portrait: false,
  domain: 'wowpizza.world',
  link: 'https://wowpizza.world/',
};

const projects = [
  {
    title: 'Timezone Dialer',
    subtitle: "See a contact's local time before you call",
    period: '2025',
    kind: 'Android app',
    context: 'Live on Google Play',
    tags: ['Flutter', 'Dart', 'Android SDK', 'Play Store'],
    points: [
      'Works out each contact\u2019s country and dialling code from their number, on device, with no lookup',
      'Shows their local time, weekday and offset, with the screen tinted by their time of day',
      'Answers the only question that matters: "Good time to call" or "Probably asleep"',
      'Places the call through WhatsApp, Telegram, Signal, Zoom or the normal dialler',
    ],
    shot: timezoneShot,
    portrait: true,
    domain: 'Google Play',
    link: 'https://play.google.com/store/apps/details?id=com.afrahmahmud.timezone_dialer',
  },
  {
    title: 'AI Portfolio',
    subtitle: 'Personal site with a RAG chatbot',
    period: 'Ongoing',
    kind: 'Web application',
    context: 'Personal',
    tags: ['React', 'Vite', 'Tailwind CSS', 'Vercel'],
    points: [
      'Chatbot answering from a 50+ document knowledge base',
      'Serverless endpoints on Vercel Edge Functions',
      'Contact flow wired through the Web3Forms API',
    ],
    shot: mahmudRajaImg,
    portrait: false,
    domain: 'mahmudraja.com',
    link: 'https://www.mahmudraja.com/',
  },
  // {
  //   title: 'Beary',
  //   subtitle: 'Learning the Beary language, the way Duolingo teaches',
  //   period: '2026',
  //   kind: 'Android app',
  //   context: 'Personal',
  //   tags: ['Kotlin', 'Jetpack Compose', 'Android SDK'],
  //   points: [
  //     'A full course for Beary, a regional language with almost no learning material online',
  //     'Lessons, drills, rapid fire, weak-spot practice, grammar chapters and dialogues',
  //     'An exercise generator builds questions from a lexicon of words, verbs and sentences',
  //     'Every phrase carried in English, Beary and Hindi, with audio feedback',
  //   ],
  //   shot: bearyImg,
  //   icon: null,
  //   portrait: true,
  //   domain: null,
  //   link: null,
  // },
  {
    title: 'Forest Focus',
    subtitle: 'A Pomodoro timer that grows something while you focus',
    period: '2026',
    kind: 'Android app and web',
    context: 'Personal',
    tags: ['React', 'TypeScript', 'Vite', 'Capacitor'],
    points: [
      'Plant something before a session. It grows while you focus.',
      'Leave the app or bail out and it withers, and the dead plant stays in your field as a receipt',
      'One React and TypeScript codebase, wrapped with Capacitor into an installable Android APK',
    ],
    shot: forestIcon,
    icon: forestIcon,
    portrait: true,
    domain: null,
    link: null,
  },
  {
    title: 'Mane',
    subtitle: 'Turns an AI-written plan into a routine that actually nags you',
    period: '2026',
    kind: 'Android app',
    context: 'Personal',
    tags: ['Flutter', 'Dart', 'Android SDK'],
    points: [
      'Paste a weekly plan from ChatGPT, Claude or Gemini and it parses into a real routine',
      'The parser is deliberately forgiving, so a model that drifts off the template still lands',
      'Local notifications, adherence tracking, nudges and a home-screen widget',
    ],
    shot: maneIcon,
    icon: maneIcon,
    portrait: true,
    domain: null,
    link: null,
  },
];

/* Grouped by discipline, mobile and web first. Real-time is one group of six
   rather than the spine of the page. */
const domains = [
  {
    name: 'Mobile',
    note: 'Native Android and iOS, Flutter on top, and the release work that gets it into a store.',
    items: ['Kotlin', 'Swift', 'Dart', 'Flutter', 'Android SDK', 'iOS / CallKit', 'ProGuard / 16KB', 'Play Store', 'TestFlight', 'BrowserStack'],
  },
  {
    name: 'Web',
    note: 'React front ends, and browser SDKs that have to behave inside someone else\u2019s app.',
    items: ['TypeScript', 'JavaScript', 'React', 'Vite', 'Tailwind CSS', 'WebSockets', 'Web Audio API', 'Vercel'],
  },
  {
    name: 'Backend and data',
    note: 'The APIs and stores behind the products I ship end to end.',
    items: ['Python', 'FastAPI', 'Go', 'Java', 'PostgreSQL', 'MySQL', 'Redis', 'Nginx', 'AWS', 'Firebase'],
  },
  {
    name: 'SDK craft',
    note: 'Shipping a library other teams depend on is a different job from shipping an app.',
    items: ['API design', 'Semantic versioning', 'Crash triage', 'Code reuse', 'WebView hardening', 'Secure logging'],
  },
  {
    name: 'Real-time',
    note: 'The domain I went deep on. Useful anywhere latency and media are hard.',
    items: ['WebRTC', 'SIP / VoIP', 'STUN/TURN / ICE', 'Opus', 'Simulcast / SVC', 'AEC'],
  },
  {
    name: 'Delivery and observability',
    note: 'Pipelines, dashboards, and knowing what broke before someone files a ticket.',
    items: ['Jenkins', 'n8n', 'Ansible', 'Prometheus', 'Grafana', 'Elasticsearch', 'Jira'],
  },
];

const education = [
  {
    logo: asset('/bms_logo.png'),
    degree: 'BE, Computer Science',
    school: 'BMS College of Engineering',
    place: 'Bengaluru',
    period: 'Nov 2020 to Jan 2024',
  },
  {
    logo: asset('/iswk_logo.png'),
    degree: 'Higher Secondary',
    school: 'Indian School al Wadi al Kabir',
    place: 'Muscat',
    period: 'Mar 2018 to Mar 2020',
  },
];

const EMAIL = 'afrahmahmud2014@gmail.com';

const contacts = [
  { label: 'LinkedIn', value: 'afrah-mahmud', href: 'https://www.linkedin.com/in/afrah-mahmud-965797295/', icon: 'LinkedIn' },
  { label: 'GitHub',   value: 'Afrah196',     href: 'https://github.com/Afrah196',                          icon: 'GitHub' },
];

/* India keeps one offset all year, so the clock needs no timezone database.
   Showing it -- and the gap to the reader's own clock -- is the same question
   Timezone Dialer above answers, which is the point. */
const IST_OFFSET_MIN = 330;

const gapLabel = (mins) => {
  if (mins === 0) return 'same time as you';
  const a = Math.abs(mins);
  const h = Math.floor(a / 60);
  const m = a % 60;
  const span = [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(' ');
  return `${span} ${mins > 0 ? 'ahead of' : 'behind'} you`;
};

/** Afrah's wall clock, and how far it is from the reader's. */
const useIstClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(id);
  }, []);

  const gap = IST_OFFSET_MIN + now.getTimezoneOffset();
  const there = new Date(now.getTime() + gap * 60000);
  const hh = String(there.getHours()).padStart(2, '0');
  const mm = String(there.getMinutes()).padStart(2, '0');
  return { clock: `${hh}:${mm}`, gap };
};

/* A mail link is the primary action, but a recruiter reading on a work machine
   often wants the address itself rather than a mail client. */
const CopyEmail = () => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* Clipboard blocked by the browser. The mail link beside it still works. */
    }
  };

  return (
    <div className="contact-mailrow">
      <a className="contact-mail" href={`mailto:${EMAIL}`}>{EMAIL}</a>
      <button type="button" className="contact-copy-btn" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

/* A capture is shown in the device it was taken on: web in a laptop, mobile in
   a phone. The screen has no fixed aspect ratio -- the shot sets its own height
   -- so a full-width capture is never cropped to fit a box. */
const DeviceFrame = ({ kind, empty, children }) => (
  <div className={`device device--${kind}${empty ? ' is-empty' : ''}`}>
    <div className="device-body">
      <div className="device-screen">{children}</div>
    </div>
    {kind === 'laptop' ? <div className="device-base" aria-hidden="true" /> : null}
  </div>
);

/* Real capture when there is one. Otherwise an honest, labelled slot with the
   app's own launcher icon, rather than a faked screenshot. */
const ProjectShot = ({ p }) => {
  const kind = p.portrait ? 'phone' : 'laptop';

  const inner = p.shot ? (
    <img className="device-shot" src={p.shot} alt={`${p.title} screen`} loading="lazy" />
  ) : (
    <div className="shot-slot">
      {p.icon ? <img className="shot-slot-icon" src={p.icon} alt="" aria-hidden="true" loading="lazy" /> : null}
      <span className="shot-slot-name">{p.title}</span>
      <span className="shot-slot-label">Screenshot to add</span>
    </div>
  );

  const frame = <DeviceFrame kind={kind} empty={!p.shot}>{inner}</DeviceFrame>;
  const cls = `proj-shot proj-shot--${kind}${p.shot ? '' : ' proj-shot--empty'}`;

  return p.link ? (
    <a className={cls} href={p.link} target="_blank" rel="noopener noreferrer" aria-label={`Open ${p.title}`}>
      {frame}
    </a>
  ) : (
    <div className={cls}>{frame}</div>
  );
};

const ProjectBody = ({ p }) => (
  <div className="proj-body">
    <p className="proj-kind">
      {p.kind}
      {p.context ? <span className="proj-context">{p.context}</span> : null}
    </p>
    <h3 className="proj-title">{p.title}</h3>
    <p className="proj-sub">{p.subtitle}</p>

    <ul className="proj-points">
      {p.points.map((pt) => <li key={pt}>{pt}</li>)}
    </ul>

    <div className="proj-foot">
      <div className="chips">
        {p.tags.map((t) => <Chip key={t} label={t} />)}
      </div>
      {p.link ? (
        <a className="proj-link" href={p.link} target="_blank" rel="noopener noreferrer">
          {p.domain} <span aria-hidden="true">↗</span>
        </a>
      ) : (
        <p className="proj-meta">{p.period}</p>
      )}
    </div>
  </div>
);

const Contact = () => {
  const { clock, gap } = useIstClock();

  return (
    <section id="contact" className="contact">
      <div className="contact-inner">
        <div className="contact-copy">
          <p className="eyebrow" data-reveal>Contact</p>

          <h2 className="contact-title" data-reveal>
            Open to mobile and full-stack roles.
          </h2>

          <p className="contact-note" data-reveal>
            Bengaluru or remote. Tell me what you are building and which
            platforms it has to run on, and I will tell you honestly whether I
            am the right person for it.
          </p>

          <div className="contact-actions" data-reveal>
            <a className="btn btn-solid" href={`mailto:${EMAIL}`}>Email me</a>
            <a
              className="btn btn-line"
              href={asset('/resume.pdf')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Resume <span aria-hidden="true">↗</span>
            </a>
          </div>

          <div data-reveal><CopyEmail /></div>
        </div>

        {/* Afrah's macro-keypad scene. Loads only as it comes into range. */}
        <NearViewport className="contact-scene">
          <Suspense fallback={<div className="scene-loading" aria-hidden="true" />}>
            <Spline scene={SCENE_KEYPAD} onLoad={() => ScrollTrigger.refresh()} />
          </Suspense>
        </NearViewport>

        <ul className="contact-list" data-reveal>
          {contacts.map(({ label, value, href, icon }) => (
            <li className="contact-item" key={label}>
              <span className="contact-label">{label}</span>
              <a className="contact-link" href={href} target="_blank" rel="noopener noreferrer">
                <TechIcon name={icon} size={14} />
                <span>{value}</span>
                <span className="contact-out" aria-hidden="true">↗</span>
              </a>
            </li>
          ))}

          <li className="contact-item">
            <span className="contact-label">Based in</span>
            <span>Bengaluru, India</span>
            <span className="contact-clock">
              <span className="contact-dot" aria-hidden="true" />
              {clock} IST, {gapLabel(gap)}
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
};

const App = () => {
  const root = useRef(null);
  useReveal(root);

  return (
    <div className="site" ref={root}>
      <div className="grain" aria-hidden="true" />
      <Nav />

      <main>
        <Hero />
        <Marquee />

        {/* ---- About: prose column + mono stat rail ---- */}
        <section id="about" className="sec sec--about">
          <header className="lede" data-reveal>
            <p className="eyebrow">About</p>
            <h2 className="lede-title">
              I build what people <span className="em">actually touch</span>.
            </h2>
          </header>

          <div className="about-grid">
            <div className="about-prose" data-reveal>
              <p className="about-open">
                I am a mobile and web engineer at Exotel. Most of my work is the
                client side.
              </p>

              <section className="ag">
                <h3 className="jg-label">What I own</h3>
                <ul className="ag-points">
                  <li>
                    The <span className="key">Android</span>,{' '}
                    <span className="key">iOS</span> and{' '}
                    <span className="key">Flutter</span> SDKs that other
                    developers build calling into their apps
                  </li>
                  <li>
                    The <span className="key">browser SDK</span> that does the
                    same on the web
                  </li>
                </ul>
              </section>

              <section className="ag">
                <h3 className="jg-label">The unglamorous part</h3>
                <ul className="ag-points">
                  <li>Crash triage, and OEM-specific bugs on <span className="key">Xiaomi</span> handsets</li>
                  <li><span className="key">Android 14 and 15</span> compatibility, and 16KB page support</li>
                  <li>ProGuard, foreground services and permission flows</li>
                  <li>Publishing to the <span className="key">Play Store</span> and <span className="key">TestFlight</span></li>
                </ul>
              </section>

              <section className="ag">
                <h3 className="jg-label">Outside work</h3>
                <ul className="ag-points">
                  <li>
                    <span className="key">Wow Pizza</span>, a React and FastAPI
                    ordering platform: 65+ endpoints, a JWT admin dashboard and
                    live delivery pricing
                  </li>
                  <li>
                    This site, and an <span className="key">AI portfolio</span>{' '}
                    with a RAG chatbot over 50+ documents
                  </li>
                </ul>
              </section>
            </div>

            <aside className="about-side">
              <blockquote className="pull" data-reveal>
                The systems underneath happen to be real-time voice, so I have
                carried an on-call pager and learned how things fail
                <span className="em"> in production</span>, not in a diagram.
              </blockquote>

              <dl className="facts" data-reveal>
                <div><dt>Now</dt><dd>MTS-1, Exotel</dd></div>
                <div><dt>Since</dt><dd>Jan 2024</dd></div>
                <div><dt>Based</dt><dd>Bengaluru, India</dd></div>
                <div><dt>Degree</dt><dd>BE Computer Science, BMSCE</dd></div>
                <div><dt>Wants</dt><dd>Mobile / full-stack product work</dd></div>
              </dl>
            </aside>
          </div>
        </section>

        {/* ---- Experience: structured record, grouped and metric-led ---- */}
        <section id="work" className="sec">
          <header className="lede" data-reveal>
            <p className="eyebrow">Experience</p>
            <h2 className="lede-title">
              Where I have <span className="em">shipped</span>.
            </h2>
          </header>

          <div className="record">
            {jobs.map((job) => (
              <article className="job" key={job.role} data-reveal>
                <div className="job-head">
                  <div className="job-id">
                    <img
                      src={asset('/exotel_logo.avif')}
                      alt=""
                      aria-hidden="true"
                      className="job-logo"
                      loading="lazy"
                    />
                    <div>
                      <h3 className="job-role">
                        {job.role}
                        {job.grade ? <span className="job-grade">{job.grade}</span> : null}
                      </h3>
                      <p className="job-org">{job.company}</p>
                    </div>
                  </div>
                  <p className="job-when">
                    <span className="job-period">{job.period}</span>
                    <span className="job-where">{job.location} · {job.type}</span>
                  </p>
                </div>

                <ul className="job-metrics">
                  {job.metrics.map((m) => (
                    <li key={m.k}>
                      <span className="m-v">{m.v}</span>
                      <span className="m-k">{m.k}</span>
                    </li>
                  ))}
                </ul>

                <div className="job-groups">
                  {job.groups.map((g) => (
                    <section className="jg" key={g.label}>
                      <h4 className="jg-label">{g.label}</h4>
                      <ul className="jg-points">
                        {g.points.map((pt) => <li key={pt}>{pt}</li>)}
                      </ul>
                    </section>
                  ))}
                </div>

                <div className="chips job-chips">
                  {job.tags.map((t) => <Chip key={t} label={t} />)}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ---- Projects: horizontal pan driven by vertical scroll ---- */}
        <section id="projects" className="sec-pan">
          <ProjectsPan items={[leadProject, ...projects]} />
        </section>

        {/* ---- Skills: five domains, prose plus chips ---- */}
        <section id="stack" className="sec">
          <Head eyebrow="Stack" title="What I reach for.">
            Grouped by the problem it solves rather than by language.
          </Head>

          <div className="domains">
            {domains.map((d) => (
              <div className="domain" key={d.name} data-reveal>
                <div className="domain-head">
                  <h3 className="domain-name">{d.name}</h3>
                  <p className="domain-note">{d.note}</p>
                </div>
                <div className="chips chips--wide">
                  {d.items.map((i) => <Chip key={i} label={i} />)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---- Education: quiet inline rows ---- */}
        <section id="education" className="sec sec--edu">
          <h2 className="edu-heading" data-reveal>Education</h2>
          <ul className="edu-list">
            {education.map((e) => (
              <li className="edu-row" key={e.school} data-reveal>
                <img src={e.logo} alt="" aria-hidden="true" className="edu-logo" loading="lazy" />
                <div className="edu-what">
                  <p className="edu-degree">{e.degree}</p>
                  <p className="edu-school">{e.school}, {e.place}</p>
                </div>
                <p className="edu-period">{e.period}</p>
              </li>
            ))}
          </ul>
        </section>
      </main>

      {/* ---- Contact: full-bleed statement ---- */}
      <Contact />


      <footer className="footer">
        <span>Afrah Mahmud</span>
        <span>Built in React. 2026</span>
      </footer>
    </div>
  );
};

export default App;
